'use client'

import type { MenuItem, ResearchInMenu, WorkspaceInMenu, ProcessStatusType, entityAliasSchema } from '@vizible/api'
import type { KeystoneProcessingStatus } from '@/types/keystone'
import { Logger } from '@vizible/utils'
import Cookies from 'js-cookie'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { createMetricsSlice, MetricsSlice } from './slices/metrics-slice'
import { toast } from 'sonner'
import { z } from 'zod'
import {
    researchSimilarityTaskAction,
    researchMetricsTaskAction,
    fetchNewsOnlyAction,
    resetAliasPropertiesResetRebuildAction,
} from '@/actions/trigger' // Assuming this is the correct path to your server actions
import { useUserStore } from './user-store' // To get userId
import type {
    EntityAlias as FullEntityAlias,
    ResearchInMenu as ApiResearchInMenu,
    WorkspaceInMenu as ApiWorkspaceInMenu,
} from '@vizible/api'

// Assuming types might be defined elsewhere or imported from API
// For placeholder purposes:
type Thread = { id: string /* ... other fields ... */ }
type Document = { id: string /* ... other fields ... */ }
// Assume Search is ResearchInMenu type
type Search = ResearchInMenu

// Add a type for the priority status, including 'unseen'
export type WorkspacePriorityStatus = ProcessStatusType | 'unseen'

const logger = new Logger('NavigationStore')

// Cookie key for storing the current workspace ID
const CURRENT_WORKSPACE_COOKIE = 'vizible_current_workspace'

// Function to save workspace ID to cookie
const saveWorkspaceToCookie = (workspaceId: string | null) => {
    if (workspaceId) {
        logger.debug('Saving workspace ID to cookie:', workspaceId)
        Cookies.set(CURRENT_WORKSPACE_COOKIE, workspaceId, { expires: 30 }) // 30 days
    } else {
        logger.debug('Removing workspace ID from cookie')
        Cookies.remove(CURRENT_WORKSPACE_COOKIE)
    }
}

// Function to get workspace ID from cookie
const getWorkspaceFromCookie = (): string | null => {
    const workspaceId = Cookies.get(CURRENT_WORKSPACE_COOKIE)
    logger.debug('Getting workspace ID from cookie:', workspaceId)
    return workspaceId || null
}

// Helper function to determine if a search has unseen updates
const isUnseen = (search: Search): boolean => {
    const lastUpdateTime = search.lastProcessedAt || search.updatedAt
    return (
        (search.processStatus === 'complete' || search.processStatus === 'idle') &&
        !!lastUpdateTime &&
        (!search.lastSeenAt || new Date(lastUpdateTime) > new Date(search.lastSeenAt))
    )
}

// Keep type definitions as needed for existing selectors/logic that remain
type EntityAlias = z.infer<typeof entityAliasSchema>
const NO_GROUP_ID = 'NO_GROUP'

// Keep the existing initialization status type
type InitializationStatus = 'idle' | 'initializing' | 'complete' | 'error'

// New structure for active context - EXPORT this
export interface ActiveViewContext {
    searchId: string | null
    documentId: string | null
    threadId: string | null
}

// Adjust types in the store to hold full entity alias details if needed
// This might mean ResearchInMenu in the store is slightly different from ApiResearchInMenu
// or ApiResearchInMenu.entityAliasesInMenu is already FullEntityAlias[]

// Assuming ApiResearchInMenu might have entityAliasesInMenu as EntityAliasBasic[]
// We might need a store-specific type or ensure it's populated with FullEntityAlias
interface StoreResearchInMenu extends Omit<ApiResearchInMenu, 'entityAliasesInMenu'> {
    currentStep?: string
    stepMetadata?: Record<string, unknown>

    entityAliasesInMenu?: FullEntityAlias[]
}

interface StoreWorkspaceInMenu extends Omit<ApiWorkspaceInMenu, 'searches'> {
    searches: StoreResearchInMenu[]
}

// Updated Navigation State Interface
export interface NavigationState {
    // --- State Fields ---
    keystoneProcessingStatuses: Record<string, {
        similarityProcessingStatus: KeystoneProcessingStatus
        lastSimilarityProcessedAt: Date | null
    }>

    initialization: {
        status: InitializationStatus
        error: Error | null
    }
    isLoading: boolean
    currentWorkspaceId: string | null
    workspaces: StoreWorkspaceInMenu[] // Use the store-specific type
    activeViewContext: ActiveViewContext
    pageTitle: string | null
    isSearchSheetOpen: boolean // Renamed
    searchSheetMode: 'create' | 'edit' | 'edit-title' | null // Updated type
    editingSearchId: string | null // Replaced editingSearch object
    systemThreads: Array<{ id: string; title: string; workspaceId: string | null }> // Keep
    lastRefreshed: Date | null // Keep
    breadcrumbHistory: Array<{ id: string; title: string; type: string }> // Keep

    // New state for dashboard actions
    dashboardSelectedAction: 'full' | 'similarity' | 'metrics' | 'fetchNews' | 'reset' | 'full-reset'
    isDashboardActionRunning: boolean
    currentDashboardActionRunId: string | null // To communicate runId back to UI if needed
    currentDashboardActionToken: string | null // To communicate token back to UI if needed
    isDashboardUIBusy: boolean

    // Chat panel state
    isChatCollapsed: boolean

    // Data refresh trigger - incremented when a research process completes to signal pages to refetch
    dataRefreshTrigger: { searchId: string; timestamp: number } | null

    // --- Actions ---
    // Initialization
    setInitializationState: (state: { status: InitializationStatus; error?: Error | null }) => void
    // Loading
    setIsLoading: (isLoading: boolean) => void
    // Workspace
    setWorkspaces: (workspaces: StoreWorkspaceInMenu[]) => void
    setCurrentWorkspaceId: (workspaceId: string | null) => void // New action signature needed
    initializeWorkspaces: (workspaces: StoreWorkspaceInMenu[]) => void // Keep existing? Review logic
    // Active Context
    setActiveContext: (context: Partial<ActiveViewContext>) => void // New action signature needed
    // Page Title
    setPageTitle: (title: string | null) => void // New action signature needed
    // Search Sheet
    setIsSearchSheetOpen: (isOpen: boolean) => void // Renamed action signature needed
    setSearchSheetMode: (mode: 'create' | 'edit' | 'edit-title' | null) => void // Updated type
    setEditingSearchId: (searchId: string | null) => void // New action signature needed
    // Existing actions to potentially keep/modify
    setSystemThreads: (threads: Array<{ id: string; title: string; workspaceId: string | null }>) => void
    updateLastRefreshed: (date: Date) => void
    setBreadcrumbHistory: (history: Array<{ id: string; title: string; type: string }>) => void
    clearNavigation: () => void // Needs updating

    // Keystone processing status actions
    setKeystoneProcessingStatus: (keystoneId: string, status: KeystoneProcessingStatus, lastProcessedAt?: string) => void

    // New actions for dashboard controls
    setDashboardSelectedAction: (action: 'full' | 'similarity' | 'metrics' | 'fetchNews' | 'reset' | 'full-reset') => void
    setDashboardRunDetails: (runId: string | null, token: string | null) => void
    clearDashboardRunDetails: () => void
    setIsDashboardUIBusy: (isBusy: boolean) => void

    // Chat panel actions
    setIsChatCollapsed: (collapsed: boolean) => void
    toggleChatCollapsed: () => void

    // Data refresh trigger action - call when a research process completes
    triggerDataRefresh: (searchId: string) => void

    // Selectors/Getters (keep, but implementations might need updating based on new state)
    getWorkspaceIndicators: (workspaceId: string) => { priorityStatus: WorkspacePriorityStatus; unseenCount: number }
    getSearchStatus: (searchId: string) => { processStatus: ProcessStatusType; lastProcessedAt: Date | null }
    // Example getter - evaluate if still needed and how to implement
    // getSortedEntityAliases: () => EntityAlias[]

    // New actions for entity aliases
    setEntityAliasesForSearch: (searchId: string, aliases: FullEntityAlias[]) => void
}

// Export the combined type for use in selectors
export type NavigationStoreState = NavigationState & MetricsSlice

// Use the combined type in the create function
export const useNavigationStore = create<NavigationStoreState>()(
    devtools(
        (set, get, api) => ({
            // Combine the metrics slice, passing set, get, api
            ...createMetricsSlice(set, get, api),

            // Initial state (Refactored) ---
            keystoneProcessingStatuses: {},
            initialization: { status: 'idle', error: null },
            isLoading: false,
            currentWorkspaceId: null,
            workspaces: [], // Initialize with the correct type
            activeViewContext: { searchId: null, documentId: null, threadId: null },
            pageTitle: null,
            isSearchSheetOpen: false, // Renamed
            searchSheetMode: null, // Initial state still null
            editingSearchId: null, // Replaced editingSearch object
            systemThreads: [],
            lastRefreshed: null,
            breadcrumbHistory: [],

            // Initial state for new fields
            dashboardSelectedAction: 'full',
            isDashboardActionRunning: false,
            currentDashboardActionRunId: null,
            currentDashboardActionToken: null,
            isDashboardUIBusy: false, // Initialize as not busy

            // Chat panel initial state
            isChatCollapsed: false,

            // Data refresh trigger initial state
            dataRefreshTrigger: null,

            // --- ACTIONS (Refactored) ---

            // Initialization
            setInitializationState: stateUpdate => {
                logger.debug('Setting initialization state:', stateUpdate)
                set(state => ({
                    initialization: {
                        ...state.initialization,
                        ...stateUpdate,
                        error: stateUpdate.error || null, // Ensure error is null if not provided
                    },
                }))
            },

            // Loading
            setIsLoading: isLoading => {
                // logger.debug('>>> setIsLoading called:', isLoading, new Error().stack) // Optional logging
                set({ isLoading })
            },

            // Workspace
            setWorkspaces: workspaces => set({ workspaces }),

            setCurrentWorkspaceId: workspaceId => {
                saveWorkspaceToCookie(workspaceId) // Persist preference
                set({ currentWorkspaceId: workspaceId })

                // When switching workspace, automatically select the most recent thread
                const currentState = get()
                const newWorkspace = currentState.workspaces.find(w => w.id === workspaceId)
                let mostRecentThreadId: string | null = null

                if (newWorkspace?.threads && newWorkspace.threads.length > 0) {
                    // Sort threads by updatedAt DESC and get the most recent one
                    const sortedThreads = [...newWorkspace.threads].sort((a, b) =>
                        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                    )
                    mostRecentThreadId = sortedThreads[0].id
                }

                // Set active context with most recent thread (if any)
                set({ activeViewContext: { searchId: null, documentId: null, threadId: mostRecentThreadId } })
            },

            // Simplified initializeWorkspaces - only sets the list
            // Determination of currentWorkspaceId is handled elsewhere (AuthLayout init, switchWorkspace)
            initializeWorkspaces: (newWorkspacesData: StoreWorkspaceInMenu[]) => {
                logger.debug('Store: Setting/Replacing workspaces list', newWorkspacesData)
                set({ workspaces: newWorkspacesData })
                // Optionally reset context here too? Or rely on setCurrentWorkspaceId?
                // set({ activeViewContext: { searchId: null, documentId: null, threadId: null } });
            },

            // Active Context
            setActiveContext: contextUpdate => {
                set(state => ({
                    activeViewContext: {
                        ...state.activeViewContext,
                        ...contextUpdate,
                    },
                }))
            },

            // Page Title
            setPageTitle: title => set({ pageTitle: title }),

            // Search Sheet
            setIsSearchSheetOpen: isOpen => set({ isSearchSheetOpen: isOpen }),
            setSearchSheetMode: mode => set({ searchSheetMode: mode }),
            setEditingSearchId: searchId => set({ editingSearchId: searchId }),

            // Existing actions to potentially keep/modify
            setSystemThreads: threads => set({ systemThreads: threads }),
            updateLastRefreshed: date => set({ lastRefreshed: date }),
            setBreadcrumbHistory: history => set({ breadcrumbHistory: history }),

            // Keystone processing status actions
            setKeystoneProcessingStatus: (keystoneId: string, status: KeystoneProcessingStatus, lastProcessedAt?: string) => {
                logger.debug(`📝 Setting keystone processing status in zustand:`, {
                    keystoneId,
                    status,
                    lastProcessedAt,
                    currentStatuses: Object.keys(get().keystoneProcessingStatuses)
                })
                set(state => ({
                    keystoneProcessingStatuses: {
                        ...state.keystoneProcessingStatuses,
                        [keystoneId]: {
                            similarityProcessingStatus: status,
                            lastSimilarityProcessedAt: lastProcessedAt ? new Date(lastProcessedAt) : null
                        }
                    }
                }))
                // Log the updated state
                const updatedState = get()
                logger.debug(`✅ Updated keystone processing statuses:`, {
                    keystoneId,
                    newStatus: updatedState.keystoneProcessingStatuses[keystoneId],
                    allStatuses: updatedState.keystoneProcessingStatuses
                })
            },

            // Clear Navigation - Updated to reset new fields
            clearNavigation: () => {
                saveWorkspaceToCookie(null) // Clear cookie

                set({
                    currentWorkspaceId: null,
                    workspaces: [],
                    activeViewContext: { searchId: null, documentId: null, threadId: null }, // Reset
                    pageTitle: null, // Reset
                    isSearchSheetOpen: false,
                    searchSheetMode: null,
                    editingSearchId: null,
                    systemThreads: [],
                    breadcrumbHistory: [],
                    isLoading: false,
                    initialization: { status: 'idle', error: null }, // Reset init status
                    keystoneProcessingStatuses: {}, // Reset keystone statuses
                    lastRefreshed: null,
                    dashboardSelectedAction: 'full',
                    isDashboardActionRunning: false,
                    currentDashboardActionRunId: null,
                    currentDashboardActionToken: null,
                    isDashboardUIBusy: false,
                    isChatCollapsed: false,
                })
            },

            // New actions for dashboard controls
            setDashboardSelectedAction: action =>
                set({
                    dashboardSelectedAction: action,
                    // Do not reset run details here, let clearDashboardRunDetails handle it
                }),

            setDashboardRunDetails: (runId, token) =>
                set({
                    currentDashboardActionRunId: runId,
                    // Ensure token is either a string or explicitly null if not a valid string
                    currentDashboardActionToken: typeof token === 'string' && token.trim() !== '' ? token : null,
                    isDashboardUIBusy: !!runId,
                }),

            clearDashboardRunDetails: () =>
                set({
                    currentDashboardActionRunId: null,
                    currentDashboardActionToken: null,
                    isDashboardUIBusy: false, // Action is complete or errored, UI is no longer busy from this action
                }),

            setIsDashboardUIBusy: isBusy =>
                set({
                    isDashboardUIBusy: isBusy,
                }),

            // Chat panel actions
            setIsChatCollapsed: collapsed => set({ isChatCollapsed: collapsed }),

            toggleChatCollapsed: () => {
                const state = get()
                set({ isChatCollapsed: !state.isChatCollapsed })
            },

            // Data refresh trigger - signal to pages that they should refetch data
            triggerDataRefresh: (searchId: string) => {
                logger.info(`Triggering data refresh for search: ${searchId}`)
                set({ dataRefreshTrigger: { searchId, timestamp: Date.now() } })
            },

            // New actions for entity aliases
            setEntityAliasesForSearch: (searchId, aliases) => {
                set(state => ({
                    workspaces: state.workspaces.map(ws => ({
                        ...ws,
                        searches: ws.searches.map(search => {
                            if (search.id === searchId) {
                                console.log(`[NavigationStore] Updating entityAliasesInMenu for searchId: ${searchId}`, aliases)
                                return { ...search, entityAliasesInMenu: aliases }
                            }
                            return search
                        }),
                    })),
                }))
            },

            // --- REMOVED ACTIONS (Logic moved to useNavigation) ---
            // setCurrentThread (replaced by setActiveContext)
            // setCurrentPath (replaced by usePathname)
            // setCurrentSearch (replaced by setActiveContext)
            // setCurrentSearchItem (context/URL driven)
            // setSearchMode (replaced by setSearchSheetMode)
            // setEditingSearch (replaced by setEditingSearchId)
            // updateSearchTitle
            // updateResearchProjectStatus
            // markResearchProjectAsSeen
            // revertMarkResearchProjectAsSeen
            // getPersistedWorkspace (handled by useNavigation)
            // getPersistedPath (handled by useNavigation)

            // --- SELECTORS / GETTERS (Implementations need review) ---
            getWorkspaceIndicators: workspaceId => {
                const state = get() // Use get() inside devtools wrapper
                const workspace = state.workspaces.find(ws => ws.id === workspaceId)

                if (!workspace || !workspace.searches || workspace.searches.length === 0) {
                    return { priorityStatus: 'idle', unseenCount: 0 }
                }
                let highestPriority: WorkspacePriorityStatus = 'idle'
                let count = 0
                let hasProcessing = false
                let hasError = false

                for (const search of workspace.searches) {
                    if (search.processStatus === 'error') {
                        hasError = true
                    }
                    if (search.processStatus === 'processing') {
                        hasProcessing = true
                    }
                    if (isUnseen(search)) {
                        count++
                    }
                }

                if (hasError) {
                    highestPriority = 'error'
                } else if (hasProcessing) {
                    highestPriority = 'processing'
                } else if (count > 0) {
                    highestPriority = 'unseen'
                } // else it remains 'idle'

                return { priorityStatus: highestPriority, unseenCount: count }
            },

            getSearchStatus: searchId => {
                const state = get()
                const search = state.workspaces.flatMap(ws => ws.searches).find(s => s.id === searchId)
                return {
                    processStatus: search?.processStatus ?? 'idle',
                    lastProcessedAt: search?.lastProcessedAt ?? null,
                }
            },

            // getSortedEntityAliases: () => { ... } // Requires review - depends on activeViewContext.searchId now
        }),
        { name: 'NavigationStore' } // Optional: Add devtools options like name
    ) // End devtools wrapper
)

// Type export might be useful
// export type NavigationStore = ReturnType<typeof useNavigationStore.getState>
